import posthog, { CaptureResult, CapturedNetworkRequest } from 'posthog-js';

const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let isInitialized = false;

const redactSensitivePath = (path: string): string => {
  return path.replace(
    /^\/(reset-password|verify-email)\/[^/?#]+/,
    '/$1/[REDACTED]'
  );
};

const sanitizeUrl = (value: string): string => {
  try {
    const url = new URL(value, window.location.origin);
    url.pathname = redactSensitivePath(url.pathname);
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return redactSensitivePath(value.split(/[?#]/)[0]);
  }
};

const sanitizeEvent = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return null;

  const properties = { ...event.properties };
  const urlProperties = [
    '$current_url',
    '$referrer',
    '$initial_current_url',
    '$initial_referrer',
    'url'
  ];

  urlProperties.forEach((property) => {
    const value = properties[property];
    if (typeof value === 'string') {
      properties[property] = sanitizeUrl(value);
    }
  });

  ['path', 'previous_path', 'redirect_path', '$pathname'].forEach((property) => {
    const value = properties[property];
    if (typeof value === 'string') {
      properties[property] = redactSensitivePath(value.split(/[?#]/)[0]);
    }
  });

  // Recipe names can contain customer content. Route and recipe IDs are enough
  // to understand product usage without sending those names to analytics.
  delete properties.recipe_name;
  delete properties.old_name;
  delete properties.new_name;
  properties.$title = 'Kitchen';

  return { ...event, properties };
};

const sanitizeRecordedRequest = (
  request: CapturedNetworkRequest
): CapturedNetworkRequest => {
  request.name = sanitizeUrl(request.name);

  // Keep timings useful in replay without ever recording headers or payloads.
  delete request.requestHeaders;
  delete request.responseHeaders;
  delete request.requestBody;
  delete request.responseBody;

  return request;
};

export const initializeAnalytics = (): boolean => {
  if (isInitialized) return true;
  if (!POSTHOG_API_KEY || typeof window === 'undefined') return false;

  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      defaults: '2026-05-30',
      autocapture: true,
      capture_pageview: 'history_change',
      capture_pageleave: true,
      disable_session_recording: false,
      mask_all_text: true,
      mask_all_element_attributes: true,
      person_profiles: 'identified_only',
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        maskCapturedNetworkRequestFn: sanitizeRecordedRequest
      },
      before_send: sanitizeEvent
    });

    isInitialized = true;
    return true;
  } catch (error) {
    console.error('Failed to initialize PostHog:', error);
    return false;
  }
};

export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
): boolean => {
  if (!isInitialized || !POSTHOG_API_KEY) return false;

  try {
    posthog.capture(eventName, {
      ...properties,
      path: redactSensitivePath(window.location.pathname)
    });
    return true;
  } catch (error) {
    console.error(`Failed to track PostHog event "${eventName}":`, error);
    return false;
  }
};

export const identifyUser = (
  userId: string,
  userProperties?: Record<string, unknown>
): void => {
  if (!isInitialized || !POSTHOG_API_KEY) return;

  try {
    posthog.identify(userId, userProperties);
  } catch (error) {
    console.error('Failed to identify user in PostHog:', error);
  }
};

export const resetUser = (): void => {
  if (!isInitialized || !POSTHOG_API_KEY) return;

  try {
    posthog.reset();
  } catch (error) {
    console.error('Failed to reset user in PostHog:', error);
  }
};

const analyticsService = {
  initializeAnalytics,
  trackEvent,
  identifyUser,
  resetUser
};

export default analyticsService;
